import { Box } from '@mui/material';
import classNames from 'classnames';
import moment from 'moment';
import React, { forwardRef, memo, useCallback, useImperativeHandle, useState } from 'react';
import { DayPickerRangeController, isInclusivelyAfterDay, isInclusivelyBeforeDay } from 'react-dates';

import config from '../../../../config';
import { START_DATE } from '../../../../util/dates';
import IconArrowHead from '../../../Icons/IconArrowHead/IconArrowHead';

export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

const arrowIconSx = (theme) => ({
  stroke: theme.tokens.marketplaceColor,
  fill: theme.tokens.marketplaceColor,
});

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = (props) => <IconArrowHead {...props} direction="left" sx={arrowIconSx} />;
const NextIcon = (props) => <IconArrowHead {...props} direction="right" sx={arrowIconSx} />;

const renderedDaySelectedSx = (theme) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: theme.tokens.DateRangeController_selectionHeight,
  backgroundColor: theme.tokens.successColor,
  color: theme.tokens.matterColorLight,
});

const inputRootSx = (theme) => ({
  [theme.breakpoints.up('md')]: {
    margin: 0,
  },

  '& .CalendarMonthGrid': {
    backgroundColor: 'transparent',
  },

  '& .DayPicker__horizontal': {
    margin: '0 auto',
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },

  '& .DayPickerNavigation__horizontal': {
    position: 'relative',
    width: '100%',
  },

  '& .DayPickerNavigation__bottom': {
    display: 'none',
    '&:first-of-type': {
      display: 'block',
    },
  },

  '& .DayPickerNavigation_button__horizontal': {
    padding: '6px 9px',
    top: '21px',
    position: 'absolute',
    cursor: 'pointer',
    display: 'inline',
    '&:first-of-type': {
      left: '24px',
    },
    '&:last-of-type': {
      right: '24px',
    },
    [theme.breakpoints.up('md')]: {
      top: '18px',
    },
  },

  '& .DayPickerNavigation_button': {
    color: theme.tokens.matterColor,
    border: 0,
  },

  '& .CalendarMonth, & .CalendarMonthGrid': {
    backgroundColor: 'transparent',
  },

  '& .DayPicker_weekHeader': {
    color: theme.tokens.matterColor,
    top: '62px',
  },

  '& .DayPicker_weekHeader_li': {
    ...theme.mixins.marketplaceH5FontStyles,
    marginTop: 0,
    marginBottom: 0,
    [theme.breakpoints.up('md')]: {
      ...theme.mixins.marketplaceH5FontStyles['@media (min-width:900px)'],
      marginTop: 0,
      marginBottom: 0,
    },
  },

  '& .DayPicker_weekHeader_li small': {
    fontSize: '100%',
  },

  '& .CalendarMonth_caption': {
    color: theme.tokens.matterColor,
    ...theme.mixins.marketplaceH3FontStyles,
    margin: '1px 0 14px',
    fontWeight: 400,
    lineHeight: '20px',
    paddingTop: '31px',
    paddingBottom: '37px',
    '&::first-letter': {
      textTransform: 'capitalize',
    },
    [theme.breakpoints.up('md')]: {
      ...theme.mixins.marketplaceH3FontStyles['@media (min-width:900px)'],
      marginTop: 0,
      marginBottom: 0,
    },
  },

  '& .CalendarDay__default': {
    border: 0,
    padding: 0,
    width: '100%',
    height: '100%',
    background: 'transparent',
  },

  '& .CalendarDay': {
    ...theme.mixins.marketplaceDefaultFontStyles,
    color: theme.tokens.matterColor,
    border: 0,
    marginTop: 0,
    marginBottom: 0,
    [theme.breakpoints.up('md')]: {
      ...theme.mixins.marketplaceDefaultFontStyles['@media (min-width:900px)'],
      marginTop: 0,
      marginBottom: 0,
    },
  },

  // Add an underline for '.renderedDay'
  '& .CalendarDay__today .renderedDay': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: theme.tokens.DateRangeController_selectionHeight,
    backgroundImage:
      'url("data:image/svg+xml;utf8,<svg width=\'14\' height=\'2\' viewBox=\'0 0 14 2\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M0 0h14v2H0z\' fill=\'%234A4A4A\' fill-rule=\'evenodd\'/></svg>")',
    backgroundPosition: 'center 28px',
  },

  '& .CalendarDay__today.CalendarDay__selected .renderedDay': {
    backgroundImage:
      'url("data:image/svg+xml;utf8,<svg width=\'14\' height=\'2\' viewBox=\'0 0 14 2\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M0 0h14v2H0z\' fill=\'%23FFF\' fill-rule=\'evenodd\'/></svg>")',
  },

  '& .CalendarDay:hover .renderedDay': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: theme.tokens.DateRangeController_selectionHeight,
    backgroundColor: theme.tokens.matterColorNegative,
    color: theme.tokens.matterColor,
  },

  // Remove default bg-color and use our extra span instead '.renderedDay'
  '& .CalendarDay__hovered_span, & .CalendarDay__selected_span': {
    backgroundColor: 'transparent',
  },

  '& .CalendarDay__hovered_span .renderedDay, & .CalendarDay__selected_span .renderedDay, & .CalendarDay__hovered_span:hover .renderedDay, & .CalendarDay__selected_span:hover .renderedDay':
    renderedDaySelectedSx(theme),

  // Remove default bg-color and use our extra span instead '.renderedDay'
  '& .CalendarDay__selected_start': {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
  },

  '& .CalendarDay__selected_start .renderedDay': {
    ...renderedDaySelectedSx(theme),
    borderTopLeftRadius: `calc(${theme.tokens.DateRangeController_selectionHeight} / 2)`,
    borderBottomLeftRadius: `calc(${theme.tokens.DateRangeController_selectionHeight} / 2)`,
  },

  // Remove default bg-color and use our extra span instead '.renderedDay'
  '& .CalendarDay__selected_end': {
    backgroundColor: 'transparent',
  },

  '& .CalendarDay__selected_end .renderedDay': {
    ...renderedDaySelectedSx(theme),
    borderTopRightRadius: `calc(${theme.tokens.DateRangeController_selectionHeight} / 2)`,
    borderBottomRightRadius: `calc(${theme.tokens.DateRangeController_selectionHeight} / 2)`,
  },

  '& .CalendarDay:hover.CalendarDay__selected_start .renderedDay, & .CalendarDay:hover.CalendarDay__selected_span .renderedDay, & .CalendarDay:hover.CalendarDay__selected_end .renderedDay':
    renderedDaySelectedSx(theme),

  '& .CalendarDay__selected_span .renderedDay': {
    ...renderedDaySelectedSx(theme),
    transition: 'all 0.2s ease-out',
  },

  // Remove default bg-color and use our extra span instead '.renderedDay'
  '& .CalendarDay__blocked_out_of_range .renderedDay, & .CalendarDay__blocked_out_of_range:hover .renderedDay':
    {
      backgroundColor: 'transparent',
      color: theme.tokens.matterColorAnti,
      textDecoration: 'line-through',
      border: 0,
    },
});

const defaultProps = {
  startDateOffset: undefined,
  endDateOffset: undefined,

  // calendar presentation and interaction related props

  orientation: HORIZONTAL_ORIENTATION,
  verticalHeight: undefined,
  withPortal: false,
  isRTL: false,
  initialVisibleMonth: null,
  firstDayOfWeek: config.i18n.firstDayOfWeek,
  numberOfMonths: 1,
  daySize: 38,
  keepOpenOnDateSelect: false,
  renderCalendarInfo: null,
  hideKeyboardShortcutsPanel: true,

  // navigation related props
  navPrev: <PrevIcon />,
  navNext: <NextIcon />,
  onPrevMonthClick() {},
  onNextMonthClick() {},
  transitionDuration: 200, // milliseconds between next month changes etc.

  renderCalendarDay: undefined, // If undefined, renders react-dates/lib/components/CalendarDay
  // day presentation and interaction related props
  renderDayContents: (day) => {
    return <span className="renderedDay">{day.format('D')}</span>;
  },
  minimumNights: config.bookingUnitType === 'line-item/night' ? 1 : 0,
  enableOutsideDays: false,
  isDayBlocked: () => false,

  // outside range -><- today ... today+available days -1 -><- outside range
  isOutsideRange: (day) => {
    const endOfRange = config.dayCountAvailableForBooking - 1;
    return (
      !isInclusivelyAfterDay(day, moment()) ||
      !isInclusivelyBeforeDay(day, moment().add(endOfRange, 'days'))
    );
  },
  isDayHighlighted: () => {},

  // Internationalization props
  // Multilocale support can be achieved with displayFormat like moment.localeData.longDateFormat('L')
  // https://momentjs.com/
  // displayFormat: 'ddd, MMM D',
  monthFormat: 'MMMM YYYY',
  weekDayFormat: 'dd',
  phrases: {}, // Add overwrites to default phrases used by react-dates
};

/**
 * Date range controller wrapping react-dates' DayPickerRangeController.
 * Exposes an imperative `onReset(startDate, endDate)` method via ref.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} [props.name] - Final Form field name (stripped before passing on)
 * @param {{ startDate?: any, endDate?: any }} [props.value] - Final Form value
 * @param {Function} [props.onChange] - called with { startDate, endDate } when both are set
 * @param {Function} [props.onFocus] - Final Form prop (stripped before passing on)
 * @param {Object} [props.meta] - Final Form prop (stripped before passing on)
 * @param {React.Ref} ref
 */
const DateRangeControllerComponent = forwardRef((props, ref) => {
  const {
    rootClassName = null,
    className = null,
    // Removing Final Form field props: name, value, onChange, onFocus, meta, children, render
    name,
    value,
    onChange,
    onFocus,
    meta,
    children,
    render,
    ...controllerProps
  } = { ...defaultProps, ...props };

  const [stateStartDate, setStateStartDate] = useState(() =>
    value && value.startDate ? moment(value.startDate) : null
  );
  const [stateEndDate, setStateEndDate] = useState(() =>
    value && value.endDate ? moment(value.endDate) : null
  );
  const [focusedInput, setFocusedInput] = useState(START_DATE);

  const onDatesChange = useCallback(
    (values) => {
      const { startDate, endDate } = values;

      const start = startDate ? startDate.toDate() : null;
      const end = endDate ? endDate.toDate() : null;

      setStateStartDate(startDate);
      setStateEndDate(endDate);

      if (startDate && endDate) {
        onChange({ startDate: start, endDate: end });
      }
    },
    [onChange]
  );

  const onFocusChange = useCallback((nextFocusedInput) => {
    // Force the focusedInput to always be truthy so that dates are always selectable
    setFocusedInput(!nextFocusedInput ? START_DATE : nextFocusedInput);
  }, []);

  const onReset = useCallback((startDate, endDate) => {
    if (startDate && endDate) {
      setStateStartDate(moment(startDate));
      setStateEndDate(moment(endDate));
    } else {
      setStateStartDate(null);
      setStateEndDate(null);
    }
    setFocusedInput(START_DATE);
  }, []);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  const startDateFromForm = value && value.startDate ? moment(value.startDate) : null;
  const endDateFromForm = value && value.endDate ? moment(value.endDate) : null;

  const isSelected = stateStartDate && stateEndDate;

  // Value given by Final Form reflects url params and is valid if both dates are set.
  // If only one date is selected state should be used to get the correct date.
  const startDate = isSelected ? startDateFromForm : stateStartDate;
  const endDate = isSelected ? endDateFromForm : stateEndDate;

  return (
    <Box
      className={classNames(rootClassName, className)}
      component="div"
      sx={rootClassName ? undefined : inputRootSx}
    >
      <DayPickerRangeController
        {...controllerProps}
        endDate={endDate}
        focusedInput={focusedInput}
        onDatesChange={onDatesChange}
        onFocusChange={onFocusChange}
        startDate={startDate}
      />
    </Box>
  );
});

DateRangeControllerComponent.displayName = 'DateRangeController';

const DateRangeController = memo(DateRangeControllerComponent);

export default DateRangeController;
